import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../../../context/AuthProvider'
import { useUserRole } from '../../../context/UserRoleProvider'
import { apiRequest } from '../../../lib/api'
import { Button } from '../../../components/ui/button'
import { Loader2, Plus, Calendar, Users, Eye, EyeOff } from 'lucide-react'

export default function ParishEvents() {
  const { user } = useAuth()
  const { getParishAdminParishId } = useUserRole()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const preselectGroupId = searchParams.get('groupId')
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [parishId, setParishId] = useState(null)
  const [showCreateForm, setShowCreateForm] = useState(!!preselectGroupId)
  const [creating, setCreating] = useState(false)

  // Lazy-load EventForm only when needed
  const EventForm = React.lazy(() =>
    import('../../../components/events/EventForm'),
  )

  const resolveParishId = async () => {
    let id = getParishAdminParishId()
    if (!id) {
      const userData = await apiRequest('/api/v1/auth/me')
      const roleDetails = userData?.roleDetails || []
      const parishAdminRole = roleDetails.find((r) => r.name === 'parish-admin')
      id = parishAdminRole?.parishId
    }
    return id
  }

  const fetchEvents = async () => {
    try {
      setLoading(true)
      setError(null)

      const id = await resolveParishId()
      if (!id) {
        setError('You are not associated with a parish as a parish admin.')
        setLoading(false)
        return
      }
      setParishId(id)

      const response = await apiRequest(
        `/api/v1/parishes/${id}/events?limit=100`,
      )
      setEvents(response.data || [])
    } catch (err) {
      console.error('Failed to fetch events:', err)
      setError(err.message || 'Failed to load events')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchEvents()
  }, [user?.id])

  const handleCreateEvent = async (data) => {
    if (!parishId) return
    try {
      setCreating(true)
      const newEvent = await apiRequest(`/api/v1/parishes/${parishId}/events`, {
        method: 'POST',
        body: JSON.stringify(data),
      })
      setShowCreateForm(false)
      // Navigate to the new event detail
      navigate(`/app/parish/events/${newEvent.id}`)
    } catch (err) {
      console.error('Failed to create event:', err)
      alert(err.message || 'Failed to create event')
    } finally {
      setCreating(false)
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  const totalRsvps = (event) =>
    (event.upcomingOccurrences || []).reduce(
      (sum, occ) => sum + (occ.rsvpCount || 0),
      0,
    )

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 text-gold animate-spin" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded p-6">
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold font-display text-charcoal">Events</h1>
        <Button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="bg-gold text-black hover:bg-gold/90"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Event
        </Button>
      </div>

      {/* Create Form */}
      {showCreateForm && (
        <div className="bg-white border border-surface-border rounded p-6 mb-6">
          <h2 className="text-xl font-semibold font-display text-charcoal mb-4">
            New Event
          </h2>
          <React.Suspense
            fallback={
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 text-gold animate-spin" />
              </div>
            }
          >
            <EventForm
              onSubmit={handleCreateEvent}
              loading={creating}
              parishId={parishId}
              initialGroupId={preselectGroupId || ''}
            />
          </React.Suspense>
        </div>
      )}

      {/* Events List */}
      {events.length === 0 ? (
        <div className="bg-white border border-surface-border rounded p-8 text-center">
          <Calendar className="w-12 h-12 text-charcoal-faint mx-auto mb-3" />
          <p className="text-charcoal-muted">No events yet. Create your first event.</p>
        </div>
      ) : (
        <div className="bg-white border border-surface-border rounded overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-surface-border">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-charcoal-muted">
                    Event
                  </th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-charcoal-muted">
                    Status
                  </th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-charcoal-muted">
                    Start Date
                  </th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-charcoal-muted">
                    Occurrences
                  </th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-charcoal-muted">
                    RSVPs
                  </th>
                </tr>
              </thead>
              <tbody>
                {events.map((event) => (
                  <tr
                    key={event.id}
                    className="border-b border-surface-border/50 hover:bg-surface-linen/50 cursor-pointer"
                    onClick={() => navigate(`/app/parish/events/${event.id}`)}
                  >
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        {event.imageUrl && (
                          <img
                            src={event.imageUrl}
                            alt=""
                            className="w-10 h-10 rounded object-cover flex-shrink-0"
                          />
                        )}
                        <div>
                          <div className="text-sm font-medium text-charcoal">
                            {event.title}
                          </div>
                          <div className="text-xs text-charcoal-muted">
                            {event.locationName}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-center">
                      {event.isPublished ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded bg-green-100 text-green-800 font-medium">
                          <Eye className="w-3 h-3" />
                          Published
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded bg-gray-100 text-gray-700 font-medium">
                          <EyeOff className="w-3 h-3" />
                          Draft
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-center text-sm text-charcoal-muted">
                      {formatDate(event.startTime)}
                    </td>
                    <td className="py-3 px-4 text-center text-sm text-charcoal-muted">
                      {event.upcomingOccurrences?.length || 0}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <div className="flex items-center justify-center gap-1 text-charcoal-muted">
                        <Users className="w-4 h-4" />
                        <span className="text-sm">{totalRsvps(event)}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
